import { Cancel, FolderOpen, Search } from '@mui/icons-material'
import { Box, Icon, IconButton, Popper, Typography, Zoom } from '@mui/material'
import React from 'react'
import { connect } from 'react-redux'
import {
  Operations as LayoutOperations,
  operations as layoutOperations
} from 'states/dux/layout/operations'
import { Payload as LayoutPayload } from 'states/dux/layout/types'
import {
  Operations as ThemeOperations,
  operations as themeOperations
} from 'states/dux/theme/operations'
import { Payload as ThemePayload } from 'states/dux/theme/types'
import {
  Operations as UserAdminOperations,
  operations as userAdminOperations
} from 'states/dux/user-admin/operations'
import { Payload as UserAdminPayload } from 'states/dux/user-admin/types'
import {
  Operations as UserOperations,
  operations as userOperations
} from 'states/dux/user/operations'
import { Payload as UserPayload } from 'states/dux/user/types'
import State from 'states/types'
import { NivosFolder } from 'types/nivos-folder'
import { mapOperationsToDispatchProps } from 'utils/common'

interface StateProps {
  theme: ThemePayload
  layout: LayoutPayload
  user: UserPayload
  userAdmin: UserAdminPayload
}

interface DispatchProps {
  operations: {
    theme: ThemeOperations
    layout: LayoutOperations
    user: UserOperations
    userAdmin: UserAdminOperations
  }
}

interface OwnProps {
  folderDetails: NivosFolder
  onClickEvent: (folder: NivosFolder) => void
}

type AppProps = StateProps & DispatchProps & OwnProps

const DocumentAdminFolderCardFC: React.FC<any> = (props: AppProps) => {
  const { folderDetails, theme, onClickEvent } = props
  const [hovered, setHovered] = React.useState(false)
  const anchorElRef = React.useRef()

  return (
    <>
      <Box
        ref={anchorElRef}
        display={'flex'}
        flexDirection={'column'}
        justifyContent={'center'}
        alignItems={'center'}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={() => {
          setHovered(false)
          onClickEvent(folderDetails)
        }}
        sx={{
          cursor: 'pointer',
          margin: 4
        }}
      >
        <Icon
          sx={{
            fontSize: 150,
            transition: '0.5s',
            marginTop: hovered ? -3 : 0
          }}
        >
          <FolderOpen fontSize={'inherit'} />
        </Icon>
        <Typography variant={'body1'} maxWidth={150}>
          {folderDetails.name}
        </Typography>
      </Box>
      {anchorElRef !== undefined && (
        <Popper
          open={hovered}
          anchorEl={anchorElRef.current}
          placement={'bottom'}
          modifiers={[
            {
              name: 'arrow',
              enabled: true
            }
          ]}
          transition
        >
          {({ TransitionProps }) => (
            <Zoom {...TransitionProps} timeout={350}>
              <Box
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                sx={{
                  backgroundColor: theme.primaryColor,
                  border: `1px solid ${theme.secondaryColor}`,
                  borderRadius: 4,
                  marginTop: -4.5
                }}
              >
                <Box
                  display={'flex'}
                  flexDirection={'column'}
                  justifyContent={'center'}
                  padding={2}
                  width={300}
                >
                  <Box
                    display={'flex'}
                    borderBottom={1}
                    borderColor={theme.secondaryColor}
                    marginBottom={2}
                    paddingY={2}
                  >
                    <Box flexGrow={1}>
                      <Typography variant={'h4'}>
                        {folderDetails.name}
                      </Typography>
                    </Box>
                    <IconButton onClick={() => setHovered(false)}>
                      <Cancel />
                    </IconButton>
                  </Box>
                  {folderDetails.files.map((item, index) => (
                    <Box
                      key={index}
                      display={'flex'}
                      alignItems={'center'}
                      paddingBottom={1}
                    >
                      <Typography align={'center'}>{item.name}</Typography>
                      <Box
                        flexGrow={1}
                        borderBottom={1}
                        height={'1px'}
                        borderColor={theme.secondaryColor}
                        marginX={2}
                      />
                      <Typography align={'center'}>
                        {item.createdAt.toLocaleDateString()}
                      </Typography>
                    </Box>
                  ))}
                  {folderDetails.files.length === 0 && (
                    <Typography align={'center'}>Empty Folder</Typography>
                  )}
                  <Box
                    display={'flex'}
                    flexDirection={'column'}
                    borderTop={1}
                    borderColor={theme.secondaryColor}
                    marginTop={2}
                    padding={2}
                    paddingBottom={0}
                  >
                    <Typography variant={'body2'}>
                      Allowed Groups: <strong>Manager</strong>
                    </Typography>
                    <Typography variant={'body2'}>
                      Allowed Users: <strong>GC GC</strong>
                    </Typography>
                    <Typography variant={'body2'}>
                      Created By:{' '}
                      <strong>{folderDetails.createdBy.fullName}</strong>
                    </Typography>
                    <Typography variant={'body2'}>
                      Created At:{' '}
                      <strong>
                        {folderDetails.createdAt.toLocaleString()}
                      </strong>
                    </Typography>
                  </Box>
                </Box>
                <Box
                  display={'flex'}
                  justifyContent={'center'}
                  alignItems={'center'}
                  padding={1}
                  borderTop={1}
                  onClick={() => {
                    setHovered(false)
                    onClickEvent(folderDetails)
                  }}
                  sx={{
                    cursor: 'pointer'
                  }}
                >
                  <Icon>
                    <Search />
                  </Icon>
                  <Typography variant={'h6'}>Open Folder</Typography>
                </Box>
              </Box>
            </Zoom>
          )}
        </Popper>
      )}
    </>
  )
}

const mapStateToProps = (state: State): StateProps => {
  return {
    theme: state.theme,
    layout: state.layout,
    user: state.user,
    userAdmin: state.userAdmin
  }
}

const mapDispatchToProps = mapOperationsToDispatchProps({
  theme: themeOperations,
  layout: layoutOperations,
  user: userOperations,
  userAdmin: userAdminOperations
})

const DocumentAdminFolderCard = connect(
  mapStateToProps,
  mapDispatchToProps
)(DocumentAdminFolderCardFC) as React.FC<OwnProps>

export default DocumentAdminFolderCard
